import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { MDText } from 'i18n-react';
import { isFunction } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';

import { ArrowLeftIcon, ArrowRightIcon, Button } from '@motorway/mw-highway-code';

import SectionHeading from 'Components/Insights/components/SectionHeading/SectionHeading';
import { useFetchSimilarVehiclesByMakeAndModel } from 'Components/Insights/hooks/useFetchSimilarVehiclesByMakeAndModel/useFetchSimilarVehiclesByMakeAndModel';
import { SeeAllVehiclesLink } from 'Components/SimilarVehiclesComponents/SeeAllVehiclesLink/SeeAllVehiclesLink';
import { SimilarVehicles as SimilarVehiclesComponent } from 'Components/SimilarVehiclesComponents/SimilarVehicles/SimilarVehicles';
import { slickSliderArrowButtonIsDisabled } from 'Components/SimilarVehiclesComponents/SimilarVehicles/SimilarVehiclesCarousel/SimilarVehiclesCarouselArrow';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import {
	SOLD_INSIGHTS_EVENT_PREFIX,
	triggerRecommendedSeeAllVehiclesClicked,
	triggerRecommendedVehicleCardClicked,
	triggerRecommendedVehicleCardIsShortlisted,
	triggerRecommendedVehiclesLoaded,
	triggerRecommendedVehiclesLoadFailed,
	triggerRecommendedVehiclesNextArrowClicked,
	triggerRecommendedVehiclesPreviousArrowClicked,
} from 'Services/analytics/events';
import type { GetSimilarVehiclesResponseWithVersion } from 'Services/api/vehicles.types';
import Skeleton from 'Storybook/core/Skeleton/Skeleton';
import { useNormaliseMakeAndModel } from 'Utilities/hooks/useNormaliseMakeAndModel/useNormaliseMakeAndModel';

import LocalTexts from './SimilarVehicles.texts';

import styles from './SimilarVehicles.module.scss';

const LocalT = new MDText(LocalTexts);

export type SimilarVehiclesProps = {
	make: string;
	modelId: number;
	onQueryUpdate?: (query: UseQueryResult<GetSimilarVehiclesResponseWithVersion>) => void;
};

export const SimilarVehicles: React.FC<SimilarVehiclesProps> = ({ make, modelId, onQueryUpdate }) => {
	const { normalisedMake, normalisedModel } = useNormaliseMakeAndModel({ make, modelId });
	const queryState = useFetchSimilarVehiclesByMakeAndModel({
		normalisedMake,
		normalisedModel,
	});

	const {
		maxWidth: { breakpointTabletMobile: isMobile },
		minWidth: {
			breakpointDesktopAccount: isAtLeastDesktopAccount,
			breakpointTablet: isAtLeastSmallDesktop,
			breakpointTabletMobile: isAtLeastTabletMobile,
		},
	} = useBreakpoint();

	// core.$space * 230 - max-width of pageSection as defined in components/Insights/Insights.helpers.scss
	const dontShowOverflow = useMediaQuery('(min-width: 1840px)');

	const buttonsContainerRef = useRef<HTMLDivElement>(null);
	const nextButtonRef = useRef<HTMLButtonElement>(null);
	const prevButtonRef = useRef<HTMLButtonElement>(null);
	const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
	const [prevButtonDisabled, setPrevButtonDisabled] = useState(false);

	let slidesToShow = 1;
	let slidesToScroll = 1;
	if (isAtLeastDesktopAccount) {
		slidesToShow = 4;
		slidesToScroll = 4;
	} else if (isAtLeastSmallDesktop) {
		slidesToShow = 3;
		slidesToScroll = 3;
	} else if (isAtLeastTabletMobile) {
		slidesToShow = 2;
		slidesToScroll = 2;
	}

	const handleReInit = () => {
		setNextButtonDisabled(slickSliderArrowButtonIsDisabled(nextButtonRef.current?.className));
		setPrevButtonDisabled(slickSliderArrowButtonIsDisabled(prevButtonRef.current?.className));
	};

	const shouldDisplaySeeAllVehicles = queryState.data && queryState.data.similarVehiclesInLiveSale > 9;

	useEffect(() => {
		if (isFunction(onQueryUpdate)) {
			onQueryUpdate(queryState);
		}
	}, [queryState, onQueryUpdate]);

	// This carousel is configured in such as way that the card images will never be bigger than 496px
	const maxImageWidth = 496;

	const handleSimilarVehiclesVisible = useCallback(() => {
		if (queryState.isSuccess) {
			triggerRecommendedVehiclesLoaded({
				enquiryIds: queryState.data.similarVehicles.map((vehicle) => vehicle.id),
				eventsPrefix: SOLD_INSIGHTS_EVENT_PREFIX,
				version: queryState.data.version,
			});
		}
	}, [queryState.data, queryState.isSuccess]);

	useEffect(() => {
		if (queryState.isError) {
			triggerRecommendedVehiclesLoadFailed({ eventsPrefix: SOLD_INSIGHTS_EVENT_PREFIX });
		}
	}, [queryState.isError]);

	return (
		<section className={styles.container}>
			<div className={styles.header}>
				{queryState.isSuccess && (
					<>
						<SectionHeading className={styles.heading} text={LocalT.translate('heading')} />
						{shouldDisplaySeeAllVehicles && !isMobile && (
							<div className={styles.headingSeeAllVehiclesLink}>
								<SeeAllVehiclesLink
									normalisedMake={normalisedMake}
									normalisedModel={normalisedModel}
									onSeeAllVehiclesLinkClick={(url: string) => {
										triggerRecommendedSeeAllVehiclesClicked(url, SOLD_INSIGHTS_EVENT_PREFIX);
									}}
									shouldUseMakeAndModelFilter={queryState.data.shouldUseMakeAndModelFilter}
								/>
							</div>
						)}
					</>
				)}
				{queryState.isLoading && <Skeleton height={4} width={35} />}
				<div ref={buttonsContainerRef} className={styles.buttonsContainer}>
					{queryState.isLoading && <Skeleton isFullWidth height={6} />}
				</div>
			</div>

			<SimilarVehiclesComponent
				key={`${normalisedMake}-${normalisedModel}`}
				customLoadingView={<Skeleton isFullWidth height={45} />}
				emptyStateClassName={styles.emptyState}
				emptyStateDescription={LocalT.translate('emptyState.description')}
				emptyStateTitle={LocalT.translate('emptyState.title')}
				events={{
					onNextArrowClick: () => triggerRecommendedVehiclesNextArrowClicked(SOLD_INSIGHTS_EVENT_PREFIX),
					onPrevArrowClick: () => triggerRecommendedVehiclesPreviousArrowClicked(SOLD_INSIGHTS_EVENT_PREFIX),
					onShortlisted: (id, url, isChecked) => {
						triggerRecommendedVehicleCardIsShortlisted({
							eventsPrefix: SOLD_INSIGHTS_EVENT_PREFIX,
							id,
							isChecked,
							url,
						});
					},
					onSimilarVehiclesVisible: handleSimilarVehiclesVisible,
					onVehicleCardClick: (id, url) => {
						triggerRecommendedVehicleCardClicked({ eventsPrefix: SOLD_INSIGHTS_EVENT_PREFIX, id, url });
					},
				}}
				maxImageWidth={maxImageWidth}
				queryState={queryState}
				sliderSettings={{
					arrows: Boolean(isAtLeastTabletMobile),
					dots: Boolean(isMobile),
					draggable: !isAtLeastSmallDesktop,
					hideDefaultNextArrow: true,
					hideDefaultPrevArrow: true,
					nextArrow:
						buttonsContainerRef.current !== null ? (
							<button ref={nextButtonRef}>
								{createPortal(
									<Button
										aria-label={`${LocalT.translate('nextButtonLabel')}`}
										disabled={nextButtonDisabled}
										icon={ArrowRightIcon}
										label={LocalT.translate('nextButtonLabel')}
										showLabel={false}
										variant="tertiary"
									/>,
									buttonsContainerRef.current,
								)}
							</button>
						) : undefined,
					onReInit: handleReInit,
					prevArrow:
						buttonsContainerRef.current !== null ? (
							<button ref={prevButtonRef}>
								{createPortal(
									<Button
										aria-label={`${LocalT.translate('prevButtonLabel')}`}
										disabled={prevButtonDisabled}
										icon={ArrowLeftIcon}
										label={LocalT.translate('prevButtonLabel')}
										showLabel={false}
										variant="tertiary"
									/>,
									buttonsContainerRef.current,
								)}
							</button>
						) : undefined,
					showOverflow: !dontShowOverflow,
					slidesToScroll,
					slidesToShow,
					swipeToSlide: true,
				}}
				vehicleCardClassName={queryState.isLoading ? styles.vehicleCardLoading : undefined}
			/>

			{shouldDisplaySeeAllVehicles && isMobile && (
				<div className={styles.bottomSeeAllVehiclesLink}>
					<SeeAllVehiclesLink
						normalisedMake={normalisedMake}
						normalisedModel={normalisedModel}
						onSeeAllVehiclesLinkClick={(url: string) => {
							triggerRecommendedSeeAllVehiclesClicked(url, SOLD_INSIGHTS_EVENT_PREFIX);
						}}
						shouldUseMakeAndModelFilter={queryState.data.shouldUseMakeAndModelFilter}
					/>
				</div>
			)}
		</section>
	);
};
